/* eslint-disable no-param-reassign */

// node modules
import { encodeObjUrl, PaginationAccessor, SearchkitManager } from 'searchkit';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

// app modules: constants
import { TIER_SCOPED_FILTERS, TOUCHED_FILTERS_STATE_KEY } from 'constants/search';

// app modules: services
import { sendEvent } from 'services/analytics';
import OwnedFiltersTransport from './OwnedFiltersTransport';

class SearchkitManagerCustom extends SearchkitManager {
  constructor(host, options = {}) {
    super(host, {
      ...options,
      transport:
        options.transport ||
        new OwnedFiltersTransport(host, {
          headers: options.httpHeaders,
          basicAuth: options.basicAuth,
          searchUrlPath: options.searchUrlPath,
          timeout: options.timeout,
          withCredentials: options.withCredentials,
        }),
    });
    // Track the last book context to detect significant context changes
    this.lastBookFilter = null;
    // One-shot: consumed by the next URL write, so a marker cannot outlive it.
    this.pendingLocationState = undefined;
    // The last query that actually went out, as distinct from `this.query`.
    this.sentQuery = undefined;
    this.sentQueryString = undefined;
    // The query text behind `this.results`.
    this.resultsQueryString = undefined;
    // Whether the search box has been edited since the last navigation began, which makes its
    // text newer than the URL that navigation is about to land on.
    this.queryBeingTyped = false;
  }

  /**
   * The tier-scoped filter controls the reader has operated on this search. Reads the pending
   * write in preference to the address bar, because `performSearch` builds and runs the query
   * before `writeHistory` settles the URL.
   * @return {Array.<String>}
   */
  touchedFilterFields() {
    const entry = (this.history && this.history.location.state) || {};
    const source = this.pendingLocationState === undefined ? entry : this.pendingLocationState;
    return source[TOUCHED_FILTERS_STATE_KEY] || [];
  }

  /**
   * Records that the reader operated one filter control. Merging over the same source
   * `touchedFilterFields` reads keeps a mark from resurrecting a stale entry value, and keeps it
   * from clobbering a search-intent write that is already pending.
   * @param  {String} field
   */
  markFilterTouched(field) {
    if (!this.history || !TIER_SCOPED_FILTERS.includes(field)) {
      return;
    }
    const base =
      this.pendingLocationState === undefined
        ? this.history.location.state || {}
        : this.pendingLocationState;
    const touched = base[TOUCHED_FILTERS_STATE_KEY] || [];
    if (touched.includes(field)) {
      return;
    }
    this.pendingLocationState = {
      ...base,
      [TOUCHED_FILTERS_STATE_KEY]: [...touched, field],
    };
  }

  /**
   * Extracts book context from current query/filters to detect significant context changes
   * @private
   * @return {string|null} Book context identifier
   */
  getBookFilter() {
    try {
      // Check query accessor for book-related search terms
      const query = this.accessors?.queryAccessor?.state?.value || '';

      // Look for book-related patterns in the search query
      const bookMatch = query.match(/book[:\s](\w+)/i);
      if (bookMatch) {
        return bookMatch[1];
      }

      // Check for ISBN patterns
      const isbnMatch = query.match(/isbn[:\s]([\w-]+)/i);
      if (isbnMatch) {
        return isbnMatch[1];
      }

      // Check stateful accessors for book-related filters
      if (this.accessors?.statefulAccessors) {
        const { statefulAccessors } = this.accessors;

        // Check for book-related filter states - including book_full_title
        const bookRelatedKeys = ['book', 'book_isbn', 'book_title', 'book_full_title', 'author'];
        const foundKey = bookRelatedKeys.find(key => {
          if (statefulAccessors[key] && statefulAccessors[key].state?.value) {
            const { value } = statefulAccessors[key].state;

            // Handle array values (like book_full_title: Array(1))
            if (Array.isArray(value) && value.length > 0) {
              return String(value[0]);
            }

            // Handle string values
            if (typeof value === 'string' && value.trim()) {
              return value.trim();
            }
          }
          return false;
        });

        if (foundKey) {
          const { value } = statefulAccessors[foundKey].state;

          // Handle array values (like book_full_title: Array(1))
          if (Array.isArray(value) && value.length > 0) {
            return String(value[0]);
          }

          // Handle string values
          if (typeof value === 'string' && value.trim()) {
            return value.trim();
          }
        }
      }

      // Check the current URL path for book identifiers
      if (typeof window !== 'undefined' && window.location) {
        const pathMatch = window.location.pathname.match(/\/book\/([^/]+)/);
        if (pathMatch) {
          return pathMatch[1];
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Determines if results should be cleared for a new context
   * @private
   * @return {boolean} Whether to clear results
   */
  shouldClearForNewContext() {
    const currentBookFilter = this.getBookFilter();

    // If we have a previous book context and it's different from current, clear results
    if (this.lastBookFilter && currentBookFilter !== this.lastBookFilter) {
      this.lastBookFilter = currentBookFilter;
      return true;
    }

    // Update the last book filter for next comparison
    this.lastBookFilter = currentBookFilter;
    return false;
  }

  /**
   * Clears the search results to prevent flash of old results
   * @private
   */
  _clearResults() {
    if (this.results && this.results.hits) {
      this.results = {
        ...this.results,
        hits: {
          ...this.results.hits,
          hits: [],
        },
      };
      this.resultsEmitter.trigger(this.results);
    }
  }

  /**
   * @override
   * @return {Promise}
   */
  _search() {
    // Only clear existing results for a new search (first page), not for pagination
    const pAccessor =
      this.accessors && this.accessors.statefulAccessors && this.accessors.statefulAccessors.p;
    const pageValue = (pAccessor && pAccessor.state && Number(pAccessor.state.value)) || 0;

    const fromVal =
      this.query && typeof this.query.getFrom === 'function' ? this.query.getFrom() : undefined;
    const isPagination = pageValue > 0 || (typeof fromVal === 'number' && fromVal > 0);

    // Only clear for significant context changes, not minor query modifications
    if (!isPagination && this.shouldClearForNewContext()) {
      this._clearResults();
    }

    // An accessor registering replaces `this.query` with no request behind it. Left in place it
    // passes for the last search sent, and the dedupe in the parent swallows the next real one:
    // the box shows the new text while the results stay on the old.
    if (this.query !== this.sentQuery) {
      delete this.query;
    }

    // Call the parent _search method
    const searchPromise = super._search();

    if (this.query !== this.sentQuery) {
      this.sentQuery = this.query;
      this.sentQueryString = this.accessors?.queryAccessor?.state?.value;
    }

    return searchPromise;
  }

  /**
   * Adds third argument to the original method: updateHistory.
   * @override
   * @param  {Boolean} replaceState
   * @param  {Boolean} notifyState
   * @param  {Boolean} updateHistory
   * @return {Promise}
   */
  performSearch(replaceState = false, notifyState = true, updateHistory = this.options.useHistory) {
    if (notifyState && !isEqual(this.accessors.getState(), this.state)) {
      this.accessors.notifyStateChange(this.state);
    }

    const searchPromise = this._search();

    if (updateHistory) {
      this.writeHistory(replaceState);
    } else {
      // A non-history search must not leave a stale override behind.
      this.pendingLocationState = undefined;
    }

    return searchPromise;
  }

  /**
   * Opens the search view without committing a query to it. Until the search
   * settles the URL must not describe a half-typed query: Chrome records every
   * distinct URL in the store behind its omnibox even when the write only
   * replaces the back-stack entry, and `SearchProvider` re-reads the query
   * string whenever a navigation completes. The filter marker is left behind: it belongs to the
   * search that earned it, and a blank search screen has no filters to have been touched.
   */
  openSearchView() {
    if (!this.history) {
      return;
    }
    const { pathname } = this.options.getLocation();
    if (this.history.location.pathname === pathname) {
      return;
    }
    const { state } = this.history.location;
    this.history.push(pathname, state && omit(state, TOUCHED_FILTERS_STATE_KEY));
  }

  /**
   * Writes the current search state to the URL. Split out of `performSearch` so a
   * caller can settle the URL on its own schedule: the search box runs a query on
   * every throttled keystroke, and each of those is far too often to be worth a
   * browser history entry.
   * @param  {Boolean} replaceState
   */
  writeHistory(replaceState = false) {
    if (!this.history) {
      return;
    }

    const overrideState = this.pendingLocationState;
    const url = `${this.options.getLocation().pathname}?${encodeObjUrl(this.state)}`;
    const { pathname, search, state } = this.history.location;
    const locationState = overrideState === undefined ? state : overrideState;

    // Rewriting the URL it already has still costs a browser history record, so
    // the omnibox fills with duplicates of the search the user is looking at.
    // An empty state encodes to a bare '?', which is the same address as no query.
    // Returning before the override is spent leaves the one-shot for the write
    // that can actually carry it.
    if (url.replace(/\?$/, '') === `${pathname}${search}` && isEqual(locationState, state)) {
      return;
    }

    this.pendingLocationState = undefined;
    const historyMethod = replaceState ? this.history.replace : this.history.push;
    historyMethod.call(this.history, url, locationState);

    // If this search is different from the last one, then report it as a new search session.
    // We ignore the p (page) parameter.
    const { p, ...currentSearch } = this.state;
    if (
      typeof this.searchSessionListener === 'function' &&
      !isEqual(this.searchSession, currentSearch)
    ) {
      this.searchSessionListener({ state: currentSearch, results: this.results });
      this.searchSession = currentSearch;
    }
  }

  /**
   * Re-runs the current query without touching history. Dropping `query` is what
   * defeats the identical-query dedupe in `_search`, for state the accessors do
   * not own (the diet Prefer boost is applied in the query processor).
   * @return {Promise}
   */
  reloadWithoutHistory() {
    delete this.query;
    return this.performSearch(true, true, false);
  }

  /**
   * Runs a failed search again, keeping the reader's query and filters. A failure drops the
   * results, so a later page would have nothing to append to and it restarts from the first.
   * @return {Promise}
   */
  retrySearch() {
    const paginationAccessor = this.getAccessorByType(PaginationAccessor);
    if (paginationAccessor) {
      paginationAccessor.resetState();
    }
    this.error = null;
    return this.reloadWithoutHistory();
  }

  /**
   * @override
   * @param {Error} error
   */
  setError(error) {
    sendEvent({
      event: 'Search.Error',
      reason: error.code || error.response?.status || error.message,
    });
    super.setError(error);
  }

  /**
   * @override
   * @param {Object} results
   */
  setResults(results) {
    this.resultsQueryString = this.sentQueryString;
    if (typeof this.query.getFrom() !== 'undefined') {
      results.hits = Object.assign({}, results.hits, {
        hits: [...this.results.hits.hits, ...results.hits.hits],
        hasChanged: false,
      });

      this.results = Object.assign({}, this.results, { hits: results.hits });
    } else {
      this.compareResults(this.results, results);
      this.results = results;
    }

    this.error = null;
    this.accessors.setResults(results);
    this.onResponseChange();
    this.resultsEmitter.trigger(this.results);
  }

  listenToHistory() {
    this._unlistenHistory = this.history.listen((location, action) => {
      this.queryBeingTyped = false;
      if (action === 'POP' && location.pathname.startsWith('/search')) {
        this._searchWhenCompleted(location);
      }
    });
  }

  setSearchSessionListener(listener) {
    this.searchSessionListener = listener;
  }
}

export default SearchkitManagerCustom;
